import { CartBundleItem } from '@/ui/store/cart/types/CartBundleItem';
import { CartItem } from '@/ui/store/cart/types/CartItem';
import { CartItems } from '@/ui/store/cart/types/CartItems';

export interface CartLineBundleRef {
  extRef: string;
  name?: string;
  upgradeProgramDeviceId?: string;
  /** Position of the set in the cart; absent on orders placed before it was recorded. */
  bundleInstanceId?: string;
  /** How many of this set were bought; the lines of one instance cover all of them. */
  bundleQuantity?: number;
}

/**
 * One flat order line. `item.quantity` is the line quantity, `item.priceVariant.price` the unit
 * price and `item.discount` the per-unit discount.
 */
export interface CartLine {
  item: CartItem;
  bundle?: CartLineBundleRef;
}

interface MatchState {
  consumed: boolean[];
  bundles: CartBundleItem[];
}

interface ExpectedLine {
  sku: string;
  quantity: number;
}

const sameDeviceId = (a?: string, b?: string): boolean => (a ?? '') === (b ?? '');

const bundleGroupKey = (ref: CartLineBundleRef): string =>
  `${ref.extRef}|${ref.upgradeProgramDeviceId ?? ''}|${ref.bundleInstanceId ?? ''}`;

/**
 * Lines that share this key are one sub-item. The Nuki ID and activation code are part of it,
 * so two devices of the same SKU keep their own identity. The bundle cart items use the same
 * key, so the cart and the order pages group a set alike.
 */
export const getCartItemMergeKey = (
  item: Pick<CartItem, 'sku' | 'priceVariant' | 'discount' | 'nukiId' | 'activationCode'>,
): string =>
  [
    item.sku,
    item.priceVariant?.price ?? '',
    item.discount ?? 0,
    item.nukiId ?? '',
    item.activationCode ?? '',
  ].join('|');

/**
 * Lines the backend emits for one store bundle: one line per slot with quantity = bundle quantity
 * for physical/digital items, one quantity-1 line per slot and bundle unit for subscriptions.
 * Only checkout matches store bundles, and its adapter blends the voucher into one line per slot,
 * so these lines are never voucher-split. Order-page lines can be voucher-split; they skip store
 * matching and go through the grouping by bundle instance.
 */
const expectedLines = (bundle: CartBundleItem): ExpectedLine[] =>
  bundle.productVariants.flatMap((variant) => {
    const units = variant.subscription ? variant.quantity * bundle.quantity : variant.quantity;
    const quantity = variant.subscription ? 1 : bundle.quantity;
    return Array.from({ length: Math.max(units, 0) }, () => ({ sku: variant.sku, quantity }));
  });

const isLineOfStoreBundle = (
  line: CartLine,
  bundle: CartBundleItem,
  expected: ExpectedLine,
): boolean =>
  line.bundle?.extRef === bundle.documentExtRef &&
  line.item.sku === expected.sku &&
  line.item.quantity === expected.quantity &&
  sameDeviceId(line.bundle.upgradeProgramDeviceId, bundle.upgradeProgramDeviceId);

/**
 * Returns the indices of the lines that make up the store bundle, or null when the bundle
 * cannot be matched completely.
 */
const matchStoreBundle = (
  lines: CartLine[],
  consumed: boolean[],
  bundle: CartBundleItem,
): number[] | null => {
  const expected = expectedLines(bundle);
  if (expected.length === 0) {
    return null;
  }
  return expected.reduce<number[] | null>((claimed, entry) => {
    if (!claimed) {
      return null;
    }
    const index = lines.findIndex(
      (line, i) => !consumed[i] && !claimed.includes(i) && isLineOfStoreBundle(line, bundle, entry),
    );
    return index === -1 ? null : [...claimed, index];
  }, []);
};

/** Merges lines with the same merge key into one sub-item (new objects). */
const mergeSubItems = (items: CartItem[]): CartItem[] => {
  const merged = items.reduce((map, item) => {
    const key = getCartItemMergeKey(item);
    const existing = map.get(key);
    map.set(
      key,
      existing ? { ...existing, quantity: existing.quantity + item.quantity } : { ...item },
    );
    return map;
  }, new Map<string, CartItem>());
  return Array.from(merged.values());
};

/**
 * Splits the merged line quantities into bundle quantity and per-bundle sub-item quantity.
 * Falls back to a bundle quantity of 1 when the line quantities are not evenly divisible.
 */
const applyBundleQuantity = (
  subItems: CartItem[],
  bundleQuantity: number,
): { quantity: number; productVariants: CartItem[] } => {
  const divisible =
    bundleQuantity > 1 && subItems.every((item) => item.quantity % bundleQuantity === 0);
  if (!divisible) {
    return { quantity: 1, productVariants: subItems };
  }
  return {
    quantity: bundleQuantity,
    productVariants: subItems.map((item) => ({
      ...item,
      quantity: item.quantity / bundleQuantity,
    })),
  };
};

const toMatchedBundle = (store: CartBundleItem, lines: CartLine[]): CartBundleItem => {
  const { quantity, productVariants } = applyBundleQuantity(
    mergeSubItems(lines.map((line) => line.item)),
    store.quantity,
  );
  return {
    id: store.id,
    documentExtRef: store.documentExtRef,
    cartBundleId: store.cartBundleId,
    name: lines[0]?.bundle?.name || store.name,
    image: store.image,
    productVariants,
    quantity,
    upgradeProgramDeviceId: store.upgradeProgramDeviceId,
  };
};

const toFallbackBundle = (lines: CartLine[], index: number): CartBundleItem => {
  const ref = lines[0]?.bundle ?? { extRef: '' };
  const productVariants = mergeSubItems(lines.map((line) => line.item));
  return {
    id: ref.extRef,
    documentExtRef: ref.extRef,
    cartBundleId: `${ref.extRef}-${index}`,
    name: ref.name ?? '',
    image: productVariants.find((item) => item.image)?.image ?? null,
    productVariants,
    quantity: 1,
    upgradeProgramDeviceId: ref.upgradeProgramDeviceId,
  };
};

const matchStoreBundles = (lines: CartLine[], cartBundles: CartBundleItem[]): MatchState =>
  cartBundles.reduce<MatchState>(
    (state, store) => {
      const claimed = matchStoreBundle(lines, state.consumed, store);
      if (!claimed) {
        return state;
      }
      return {
        consumed: state.consumed.map((isConsumed, i) => isConsumed || claimed.includes(i)),
        bundles: [
          ...state.bundles,
          toMatchedBundle(
            store,
            claimed.map((i) => lines[i]),
          ),
        ],
      };
    },
    { consumed: lines.map(() => false), bundles: [] },
  );

const toSingleUnits = (line: CartLine): CartLine[] =>
  Array.from({ length: line.item.quantity }, () => ({
    ...line,
    item: { ...line.item, quantity: 1 },
  }));

/**
 * Splits the lines of a set bought N times into N sets. The lines carry no set of their own, so
 * every line is expanded into single units and the n-th unit of a SKU goes to set n mod N. That
 * deals a slot evenly even when it spans several lines (voucher split, the same product in two
 * slots, one line per subscription unit), and the sets still add up to the lines.
 */
const dealIntoSets = (lines: CartLine[]): CartLine[][] => {
  const setCount = lines[0]?.bundle?.bundleQuantity ?? 1;
  if (setCount <= 1) {
    return [lines];
  }
  const sets: CartLine[][] = Array.from({ length: setCount }, () => []);
  const dealtPerSku = new Map<string, number>();
  lines.flatMap(toSingleUnits).forEach((unit) => {
    const dealt = dealtPerSku.get(unit.item.sku) ?? 0;
    sets[dealt % setCount].push(unit);
    dealtPerSku.set(unit.item.sku, dealt + 1);
  });
  return sets.filter((set) => set.length > 0);
};

/**
 * Groups the remaining bundle lines by bundleGroupKey in first-appearance order, one group per set.
 */
const groupRemainingByBundle = (lines: CartLine[], consumed: boolean[]): CartLine[][] => {
  const groups = lines.reduce((map, line, i) => {
    if (consumed[i] || !line.bundle) {
      return map;
    }
    const key = bundleGroupKey(line.bundle);
    map.set(key, [...(map.get(key) ?? []), line]);
    return map;
  }, new Map<string, CartLine[]>());
  return Array.from(groups.values()).flatMap(dealIntoSets);
};

/**
 * Groups flat order lines into standalone products and bundles. Store bundles are matched
 * first (all or nothing, in store order); remaining bundle lines are grouped by bundle
 * reference and bundle instance, and a set bought several times becomes one bundle per set.
 * Lines without an instance id (orders placed before it was recorded) keep the grouping by
 * ext ref and device id. Every line ends up in exactly one bundle or in `products`.
 */
export function groupCartLines(lines: CartLine[], cartBundles: CartBundleItem[] = []): CartItems {
  const { consumed, bundles: matched } = matchStoreBundles(lines, cartBundles);
  const fallback = groupRemainingByBundle(lines, consumed).map((group, i) =>
    toFallbackBundle(group, matched.length + i),
  );
  const products = lines
    .filter((line, i) => !consumed[i] && !line.bundle)
    .map((line) => ({ ...line.item }));
  return { products, bundles: [...matched, ...fallback] };
}
